/**
 * Catalogos de la agenda.
 *
 * Igual que el resto de catalogos: la lista vive aqui, el tipo se deriva de
 * ella, y el backend y el frontend construyen contra lo mismo. Un enum metido
 * en el modelo de Mongoose obliga a recordar dos sitios cada vez que se agrega
 * un valor.
 */
/**
 * Dias de la semana, en ASCII y en orden de trabajo.
 *
 * Son CLAVES: viajan en JSON, se guardan en la base y nombran campos. El texto
 * que ve el medico se acentua en la interfaz, no aqui.
 */
export const DIAS_SEMANA = [
    'lunes',
    'martes',
    'miercoles',
    'jueves',
    'viernes',
    'sabado',
    'domingo',
];
/**
 * `Date.getDay()` devuelve 0 para el domingo. Este arreglo traduce de ese
 * indice a la clave del catalogo, que es como se guardan los horarios.
 */
const DIA_POR_INDICE = [
    'domingo',
    'lunes',
    'martes',
    'miercoles',
    'jueves',
    'viernes',
    'sabado',
];
/**
 * Dia de la semana de una fecha, como clave del catalogo.
 *
 * Usa la zona horaria de quien llama, asi que sirve para pintar en el navegador
 * pero NO para decidir que horario aplica en el consultorio. Para eso esta
 * `diaSemanaDeFecha`, que parte del dia de calendario ya resuelto.
 */
export function diaSemanaDe(fecha) {
    return DIA_POR_INDICE[fecha.getDay()] ?? 'lunes';
}
/**
 * Dia de la semana de una fecha AAAA-MM-DD, sin zona horaria de por medio.
 *
 * El calculo va en UTC para que no dependa de la maquina: «el 2026-09-03 es
 * jueves» es cierto en Tijuana y en Merida, y el horario de atencion del
 * consultorio se decide por eso, no por donde este el servidor.
 */
export function diaSemanaDeFecha(fecha) {
    const [anio, mes, dia] = fecha.split('-').map(Number);
    const enUtc = new Date(Date.UTC(anio ?? 1970, (mes ?? 1) - 1, dia ?? 1));
    return DIA_POR_INDICE[enUtc.getUTCDay()] ?? 'lunes';
}
/**
 * Estados de una cita.
 *
 * `solicitada` es el estado en que nace una peticion del portal publico: nadie
 * del consultorio la ha visto todavia. Una cita que crea el consultorio nace
 * `confirmada`.
 *
 * Ni `cancelada` ni `no-asistio` borran nada: una agenda es tambien un registro
 * de lo que paso, y saber que una paciente no se presento es informacion
 * clinica.
 */
export const ESTADOS_CITA = [
    'solicitada',
    'confirmada',
    'cancelada',
    'atendida',
    'no-asistio',
];
/**
 * Estados que ocupan un hueco de la agenda.
 *
 * Es la lista que decide si dos citas chocan. Una cancelada no estorba; una
 * solicitada si, porque si no el mismo hueco se ofreceria dos veces mientras el
 * consultorio decide.
 */
export const ESTADOS_CITA_VIVOS = [
    'solicitada',
    'confirmada',
    'atendida',
];
export function citaOcupaHueco(estado) {
    return ESTADOS_CITA_VIVOS.includes(estado);
}
/** Estados desde los que todavia se puede pasar a otro, y a cuales. */
const TRANSICIONES = {
    solicitada: ['confirmada', 'cancelada'],
    confirmada: ['atendida', 'no-asistio', 'cancelada'],
    // Terminales: una cita atendida ya produjo consulta, y una cancelada se
    // vuelve a agendar creando otra, no resucitando esta.
    atendida: [],
    cancelada: [],
    'no-asistio': [],
};
export function transicionValida(desde, hacia) {
    return TRANSICIONES[desde].includes(hacia);
}
export function transicionesDesde(estado) {
    return TRANSICIONES[estado];
}
/**
 * De donde vino la cita.
 *
 * Se guarda porque cambia como se trata: una del portal la pidio alguien de
 * fuera y hay que confirmarla; una del mostrador la puso el consultorio.
 */
export const ORIGENES_CITA = ['consultorio', 'portal'];
/**
 * Dias hacia adelante que puede ofrecer el portal, y dias que se pueden
 * consultar de una vez. Es el mismo numero a proposito: si la configuracion
 * admitiera mas de lo que la consulta devuelve, el portal pediria una ventana
 * que el servidor rechaza.
 */
export const MAX_DIAS_DISPONIBILIDAD = 60;
//# sourceMappingURL=agenda.js.map